import { useState, useContext, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ThemeContext } from '../App';
import { Menu, X, Sun, Moon, ChevronDown, Car, Search, DollarSign, Shield, Newspaper, HelpCircle, Building2, Zap, FileText, Info } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const { darkMode, setDarkMode } = useContext(ThemeContext);
  const location = useLocation();
  const dropdownTimeoutRef = useRef(null);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  // Navbar visibility logic:
// - Show when mouse is near top edge OR user scrolls up OR page is at very top.
// - Hide after a short delay when user scrolls down and mouse is not near the top / not hovering the navbar.
  const [isHoveringNavbar, setIsHoveringNavbar] = useState(false);

  useEffect(() => {
    // Desired behavior:
    // - Navbar is ONLY allowed to appear when the page is at (or very near) the top
    //   AND the mouse pointer is near the top edge of the viewport.
    // - While the user is interacting with the navbar (hovering), keep it visible.
    // - If the user scrolls away from the top, hide it.

    const TOP_REVEAL_PX = 24;     // mouse distance from top to reveal
    const SCROLL_TOP_PX = 6;      // treat this as "at the top" (small buffer)

    const mouseYRef = { current: Infinity };

    const syncVisibility = () => {
      const atTop = (window.scrollY || 0) <= SCROLL_TOP_PX;
      const mouseNearTop = mouseYRef.current <= TOP_REVEAL_PX;

      if (isHoveringNavbar) {
        setIsVisible(true);
        return;
      }

      setIsVisible(atTop && mouseNearTop);
      if (!(atTop && mouseNearTop)) setActiveDropdown(null);
    };

    const handleMouseMove = (e) => {
      mouseYRef.current = e.clientY;
      syncVisibility();
    };

    const handleScroll = () => {
      // In case the user scrolls to the very top without moving the mouse,
      // re-evaluate based on the last known mouse position.
      syncVisibility();
    };

    // Initial state
    syncVisibility();

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHoveringNavbar]); // depend on hovering state only

  const handleNavbarMouseEnter = () => {
    setIsHoveringNavbar(true);
    setIsVisible(true);
  };

  const handleNavbarMouseLeave = () => {
    setIsHoveringNavbar(false);
    setActiveDropdown(null);
  };

  const handleMouseEnter = (label) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 400);
  };

  const navGroups = [
    {
      label: 'Buy',
      items: [
        { label: 'Search Cars', to: '/results', icon: Search, desc: 'Browse all vehicles' },
        { label: 'Car Specials', to: '/specials', icon: DollarSign, desc: 'Latest deals & promos' },
        { label: 'Help Me Find', to: '/help-me-find', icon: HelpCircle, desc: 'We search for you' },
        { label: 'Dealers', to: '/dealers', icon: Building2, desc: 'Find dealers near you' },
        { label: 'EV Charging', to: '/ev-charging', icon: Zap, desc: 'Charging stations' }
      ]
    },
    {
      label: 'Sell',
      items: [
        { label: 'Sell Your Car', to: '/sell-your-car', icon: DollarSign, desc: 'List your vehicle' },
        { label: 'Dealer Portal', to: '/dealer-portal', icon: Building2, desc: 'For dealerships' }
      ]
    },
    {
      label: 'Finance',
      items: [
        { label: 'Affordability', to: '/affordability', icon: DollarSign, desc: 'What can I afford?' },
        { label: 'Finance Calculator', to: '/finance', icon: FileText, desc: 'Calculate payments' },
        { label: 'Insurance', to: '/insurance', icon: Shield, desc: 'Get quotes' },
        { label: 'Life Cover', to: '/life-cover', icon: Shield, desc: 'Protect your family' }
      ]
    },
    {
      label: 'More',
      items: [
        { label: 'News & Reviews', to: '/news', icon: Newspaper, desc: 'Motoring articles' },
        { label: 'Car Inspector', to: '/inspector', icon: Search, desc: 'Pre-purchase checks' },
        { label: 'Advice & FAQ', to: '/faq', icon: HelpCircle, desc: 'Common questions' },
        { label: 'About Us', to: '/about', icon: Info, desc: 'Our story' }
      ]
    }
  ];

  return (
    <>
      {/* Invisible trigger zone at top */}
      <div 
        className="fixed top-0 left-0 right-0 h-3 z-[60]"
        onMouseEnter={handleNavbarMouseEnter}
      />

      <nav 
        className={`fixed top-0 left-0 right-0 bg-gray-900/95 backdrop-blur-md border-b border-gray-700/50 z-50 transition-all duration-300 ${
          isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
        onMouseEnter={handleNavbarMouseEnter}
        onMouseLeave={handleNavbarMouseLeave}
      >
        <div className="container">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <div className="w-9 h-9 bg-gradient-to-br from-green-600 via-yellow-500 to-red-500 rounded-lg flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-base text-white leading-tight">AutoSearch</span>
                <span className="text-[9px] font-semibold text-yellow-400 -mt-1 tracking-wider">SOUTH AFRICA</span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navGroups.map((group) => (
                <div
                  key={group.label}
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(group.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className={`px-3 py-2 rounded-lg font-medium text-sm flex items-center gap-1 transition ${
                      activeDropdown === group.label
                        ? 'bg-white/10 text-yellow-400'
                        : 'text-gray-300 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {group.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === group.label ? 'rotate-180' : ''}`} />
                  </button>

                  {activeDropdown === group.label && (
                    <div 
                      className="absolute top-full left-0 w-64 bg-gray-800/95 backdrop-blur-md rounded-xl shadow-xl border border-gray-700/50 py-2 mt-1"
                      onMouseEnter={() => handleMouseEnter(group.label)}
                      onMouseLeave={handleMouseLeave}
                    >
                      {group.items.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition"
                        >
                          <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <item.icon className="w-4 h-4 text-yellow-400" />
                          </div>
                          <div>
                            <p className="font-medium text-sm text-white">{item.label}</p>
                            <p className="text-xs text-gray-400">{item.desc}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition"
                aria-label="Toggle theme"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              <Link
                to="/results"
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white rounded-lg font-medium text-sm transition"
              >
                <Search className="w-4 h-4" />
                Search
              </Link>

              <button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden border-t border-gray-700/50 bg-gray-900/95 max-h-[80vh] overflow-y-auto">
            <div className="container py-4">
              {navGroups.map((group) => (
                <div key={group.label} className="mb-4">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 px-3">
                    {group.label}
                  </p>
                  <div className="space-y-1">
                    {group.items.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition"
                      >
                        <item.icon className="w-5 h-5 text-yellow-400" />
                        <span className="font-medium text-white">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;